import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline'

/**
 * Wybór jednej opcji z listy rozwijanej — WŁASNY komponent, nie `<select>`.
 *
 * DLACZEGO NIE NATYWNY `<select>`: potrafi „zacinać się” w interfejsie (na dotyku
 * otwiera systemowy walec, na Windowsie renderuje się poza stylem strony), a jego
 * wyglądu nie da się doprowadzić do reszty formularza. Tutaj lista to zwykły
 * overlay, więc zachowuje się identycznie na każdej platformie.
 *
 * Zwinięta forma jest istotna: przy czterech opcjach rozłożonych na płasko sekcja
 * „Warunki współpracy” rosła na wysokość ekranu, a to pola, które wypełnia się raz.
 *
 * Obsługa klawiatury jest pełna (strzałki, Enter, Escape, Home/End) — bez tego
 * własny dropdown jest krokiem wstecz wobec natywnego `<select>`.
 */
const ListaRozwijana = ({ opcje, value, onChange, placeholder = 'wybierz…', ariaLabel }) => {
  const [otwarta, setOtwarta] = useState(false)
  const [podswietlona, setPodswietlona] = useState(-1)
  const ref = useRef(null)
  const listaRef = useRef(null)

  const wybrana = opcje.find((o) => String(o.value) === String(value)) || null
  const indeksWybranej = opcje.findIndex((o) => String(o.value) === String(value))

  // Zamknięcie kliknięciem poza. Bez tego lista zostaje otwarta po przejściu
  // wzrokiem gdzie indziej i wygląda na zawieszoną.
  useEffect(() => {
    if (!otwarta) return undefined
    const pozaKomponentem = (e) => { if (ref.current && !ref.current.contains(e.target)) setOtwarta(false) }
    document.addEventListener('mousedown', pozaKomponentem)
    return () => document.removeEventListener('mousedown', pozaKomponentem)
  }, [otwarta])

  // Po otwarciu podświetlamy pozycję JUŻ WYBRANĄ, nie pierwszą — inaczej strzałka
  // w dół przeskakiwałaby od początku listy zamiast od bieżącego wyboru.
  useEffect(() => {
    if (otwarta) setPodswietlona(indeksWybranej >= 0 ? indeksWybranej : 0)
  }, [otwarta, indeksWybranej])

  const wybierz = (opcja) => {
    onChange(opcja.value)
    setOtwarta(false)
    ref.current?.querySelector('button')?.focus()
  }

  const naKlawisz = (e) => {
    if (!otwarta) {
      if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
        e.preventDefault()
        setOtwarta(true)
      }
      return
    }
    if (e.key === 'Escape') { e.preventDefault(); setOtwarta(false); return }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      if (opcje[podswietlona]) wybierz(opcje[podswietlona])
      return
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); setPodswietlona((i) => Math.min(i + 1, opcje.length - 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setPodswietlona((i) => Math.max(i - 1, 0)) }
    if (e.key === 'Home') { e.preventDefault(); setPodswietlona(0) }
    if (e.key === 'End') { e.preventDefault(); setPodswietlona(opcje.length - 1) }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOtwarta((v) => !v)}
        onKeyDown={naKlawisz}
        aria-haspopup="listbox"
        aria-expanded={otwarta}
        aria-label={ariaLabel}
        className={`w-full flex items-center justify-between gap-2 px-3.5 py-2.5 rounded-lg border bg-white
                    text-sm text-left transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          otwarta ? 'border-blue-400' : 'border-gray-200 hover:border-gray-300'
        }`}
      >
        <span className={`truncate ${wybrana ? 'text-gray-900' : 'text-gray-400'}`}>
          {wybrana ? wybrana.label : placeholder}
        </span>
        <ChevronDownIcon
          className={`w-4 h-4 flex-shrink-0 text-gray-400 transition-transform ${otwarta ? 'rotate-180' : ''}`}
        />
      </button>

      {otwarta && (
        <ul
          ref={listaRef}
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          className="absolute left-0 right-0 top-full mt-1 z-30 py-1 max-h-60 overflow-y-auto
                     bg-white border border-gray-200 rounded-lg shadow-xl"
        >
          {opcje.map((o, i) => {
            const jestWybrana = String(o.value) === String(value)
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={jestWybrana}
                  onClick={() => wybierz(o)}
                  onMouseEnter={() => setPodswietlona(i)}
                  className={`w-full flex items-center justify-between gap-2 px-3.5 py-2 text-sm text-left transition-colors ${
                    i === podswietlona ? 'bg-blue-50' : ''
                  } ${jestWybrana ? 'font-semibold text-blue-700' : 'text-gray-700'}`}
                >
                  <span className="truncate">{o.label}</span>
                  {jestWybrana && <CheckIcon className="w-4 h-4 flex-shrink-0 text-blue-600" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ListaRozwijana
